/** The one way the SPA talks to the API. Throws ApiError with the status and code the UI decides on. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function api<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method: init.method ?? (init.body === undefined ? 'GET' : 'POST'),
      credentials: 'same-origin',
      headers: init.body === undefined ? undefined : { 'content-type': 'application/json' },
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
    });
  } catch {
    throw new ApiError(0, 'network', 'Could not reach Concord. Check your connection and try again.');
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    // A non-JSON body is an infrastructure fault, not the user's input.
  }
  if (!res.ok) {
    const e = (data as { error?: { code?: string; message?: string } } | null)?.error;
    if (e?.code === 'session_idle') goToSignIn('idle');
    throw new ApiError(
      res.status,
      e?.code ?? 'http_error',
      e?.message ?? `Something went wrong (${res.status}).`,
    );
  }
  return data as T;
}

/** Plain-words message for any error, with the rate-limit case spelled out. */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  return 'Something went wrong. Try again.';
}

/** The sign-in page for the part of the app the person is in, saying why they are there. */
export function signInPath(reason: 'idle'): string {
  const staff = window.location.pathname.startsWith('/console');
  return `${staff ? '/console/sign-in' : '/sign-in'}?reason=${reason}`;
}

function goToSignIn(reason: 'idle') {
  if (!/\/sign-in$/.test(window.location.pathname)) window.location.assign(signInPath(reason));
}
